import {
  FileIcon as FileIconBase,
  type FileIconProps as FileIconBaseProps,
  defaultStyles,
} from 'react-file-icon';
import { purple } from '@mui/material/colors';
import FileIconWrapper from './FileIconWrapper';
import UnknownFileIcon from './UnknownFileIcon';

export const GOOGLE_MIME_TYPE_PREFIX = 'application/vnd.google-apps.';

enum GoogleFileType {
  AUDIO = 'AUDIO',
  DOCUMENT = 'DOCUMENT',
  FORM = 'FORM',
  PHOTO = 'PHOTO',
  PRESENTATION = 'PRESENTATION',
  SPREADSHEET = 'SPREADSHEET',
  VIDEO = 'VIDEO',
  OTHER = 'OTHER',
}

const getGoogleFileType = (mimeType: string): GoogleFileType | null => {
  if (!mimeType.startsWith(GOOGLE_MIME_TYPE_PREFIX)) {
    return null;
  }

  const fileType = mimeType.slice(GOOGLE_MIME_TYPE_PREFIX.length);

  switch (fileType) {
    case 'audio': {
      return GoogleFileType.AUDIO;
    }
    case 'document': {
      return GoogleFileType.DOCUMENT;
    }
    case 'form': {
      return GoogleFileType.FORM;
    }
    case 'photo': {
      return GoogleFileType.PHOTO;
    }
    case 'presentation': {
      return GoogleFileType.PRESENTATION;
    }
    case 'spreadsheet':
      return GoogleFileType.SPREADSHEET;
    case 'vid':
    case 'video': {
      return GoogleFileType.VIDEO;
    }
    default: {
      return GoogleFileType.OTHER;
    }
  }
};

const googleFileIconStyles = {
  [GoogleFileType.AUDIO]: defaultStyles.mp3,
  [GoogleFileType.DOCUMENT]: defaultStyles.docx,
  [GoogleFileType.FORM]: {
    type: 'document',
    color: purple[800],
    glyphColor: 'rgba(255, 255, 255, 0.4)',
    extension: 'form',
    labelUppercase: true,
    labelColor: purple[900],
  },
  [GoogleFileType.PHOTO]: defaultStyles.jpeg,
  [GoogleFileType.PRESENTATION]: defaultStyles.pptx,
  [GoogleFileType.SPREADSHEET]: defaultStyles.xlsx,
  [GoogleFileType.VIDEO]: defaultStyles.mp4,
  [GoogleFileType.OTHER]: null,
} satisfies Record<GoogleFileType, Partial<FileIconBaseProps> | null>;

type GoogleFileIconProps = {
  mimeType: string;
};

const GoogleFileIcon = ({ mimeType }: GoogleFileIconProps) => {
  const googleFileType = getGoogleFileType(mimeType);

  if (googleFileType === null || googleFileType === GoogleFileType.OTHER) {
    return <UnknownFileIcon />;
  }

  const fileIconStyles = googleFileIconStyles[googleFileType];

  return (
    <FileIconWrapper>
      <FileIconBase {...fileIconStyles} />
    </FileIconWrapper>
  );
};

export default GoogleFileIcon;
